// src/components/BlogPost.js
import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, IconButton, CircularProgress, Button } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import logo from '../assets/logo.png';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageBg = styled(Box)(() => ({
  minHeight: '100vh',
  background: '#ffffff',
  display: 'flex',
  flexDirection: 'column',
}));

const Article = styled(Box)(({ theme }) => ({
  animation: `${fadeIn} 0.5s ease-out`,
  '& h2.block': {
    fontSize: '2rem',
    fontWeight: 800,
    color: '#111',
    lineHeight: 1.2,
    margin: '40px 0 16px',
    letterSpacing: '-0.02em',
  },
  '& h3.block': {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#222',
    lineHeight: 1.3,
    margin: '32px 0 12px',
  },
  '& p.block': {
    fontSize: '1.1rem',
    lineHeight: 1.85,
    color: '#333',
    marginBottom: 20,
    whiteSpace: 'pre-wrap', // preserve line breaks typed in the editor
  },
  '& blockquote.block': {
    margin: '32px 0',
    padding: '20px 28px',
    borderLeft: '4px solid #ffb71b',
    background: 'linear-gradient(90deg, #fff8e7 0%, #fffdf5 100%)',
    borderRadius: '0 12px 12px 0',
    fontSize: '1.2rem',
    fontStyle: 'italic',
    color: '#444',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap', // preserve line breaks typed in the editor
  },
  '& blockquote.block .attribution': {
    display: 'block',
    marginTop: 8,
    fontSize: '0.9rem',
    color: '#999',
    fontStyle: 'normal',
    fontWeight: 600,
  },
  '& ul.block, & ol.block': {
    margin: '16px 0 24px 0',
    paddingLeft: 28,
    fontSize: '1.05rem',
    lineHeight: 1.85,
    color: '#333',
  },
  '& ul.block li, & ol.block li': { marginBottom: 8 },
  '& hr.block': {
    border: 'none',
    borderTop: '1px solid #eee',
    margin: '40px 0',
  },
}));

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

/* Inline marks: **bold**, *italic*, [text](url) → React fragments */
function renderInline(text) {
  if (!text) return null;
  const tokens = [];
  let i = 0;
  while (i < text.length) {
    // Link
    const link = text.slice(i).match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/);
    if (link) {
      tokens.push({ type: 'link', text: link[1], url: link[2] });
      i += link[0].length;
      continue;
    }
    // Bold
    if (text.slice(i, i + 2) === '**') {
      const end = text.indexOf('**', i + 2);
      if (end > -1) {
        tokens.push({ type: 'bold', text: text.slice(i + 2, end) });
        i = end + 2;
        continue;
      }
    }
    // Italic
    if (text[i] === '*' && text[i + 1] !== '*') {
      const end = text.indexOf('*', i + 1);
      if (end > -1) {
        tokens.push({ type: 'italic', text: text.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    // Plain
    let next = text.length;
    const nextLink = text.indexOf('[', i);
    const nextBold = text.indexOf('**', i);
    const nextItalic = text.indexOf('*', i);
    [nextLink, nextBold, nextItalic].forEach(p => { if (p > -1 && p < next && p > i - 1) next = p; });
    tokens.push({ type: 'text', text: text.slice(i, next) });
    i = next;
  }
  return tokens.map((t, idx) => {
    if (t.type === 'bold') return <strong key={idx}>{t.text}</strong>;
    if (t.type === 'italic') return <em key={idx}>{t.text}</em>;
    if (t.type === 'link') return <a key={idx} href={t.url} target="_blank" rel="noopener noreferrer" style={{ color: '#ff9e00', fontWeight: 600 }}>{t.text}</a>;
    return <React.Fragment key={idx}>{t.text}</React.Fragment>;
  });
}

const imageWrapStyle = (alignment) => {
  switch (alignment) {
    case 'left': return { float: 'left', maxWidth: '50%', margin: '8px 24px 16px 0' };
    case 'right': return { float: 'right', maxWidth: '50%', margin: '8px 0 16px 24px' };
    case 'center': return { display: 'block', maxWidth: '70%', margin: '32px auto' };
    case 'full':
    default: return { display: 'block', width: '100%', margin: '32px 0' };
  }
};

function Block({ block }) {
  switch (block.type) {
    case 'heading':
      return block.level === 3
        ? <h3 className="block">{renderInline(block.text)}</h3>
        : <h2 className="block">{renderInline(block.text)}</h2>;
    case 'paragraph':
      return <p className="block">{renderInline(block.text)}</p>;
    case 'image':
      if (!block.url) return null;
      return (
        <figure style={{ margin: 0, ...imageWrapStyle(block.alignment) }}>
          <Box component="img" src={block.url} alt={block.alt || ''} sx={{ width: '100%', borderRadius: 12, display: 'block' }} />
          {block.caption && (
            <Typography component="figcaption" sx={{ textAlign: 'center', fontSize: '0.85rem', color: '#999', mt: 1, fontStyle: 'italic' }}>
              {block.caption}
            </Typography>
          )}
        </figure>
      );
    case 'quote':
      return (
        <blockquote className="block">
          {renderInline(block.text)}
          {block.attribution && <span className="attribution">— {block.attribution}</span>}
        </blockquote>
      );
    case 'list':
      const ListTag = block.ordered ? 'ol' : 'ul';
      return (
        <ListTag className="block">
          {(block.items || []).map((item, i) => (
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ListTag>
      );
    case 'divider':
      return <hr className="block" />;
    case 'two-column':
      return (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 4,
          my: 4,
          clear: 'both',
        }}>
          <Box sx={{ fontSize: '1.05rem', lineHeight: 1.85, color: '#333', whiteSpace: 'pre-wrap' }}>
            {renderInline(block.leftText)}
          </Box>
          <Box sx={{ fontSize: '1.05rem', lineHeight: 1.85, color: '#333', whiteSpace: 'pre-wrap' }}>
            {renderInline(block.rightText)}
          </Box>
        </Box>
      );
    default:
      return null;
  }
}

export default function BlogPost() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`/api/blogs/${slug}`)
      .then(r => {
        if (r.status === 404) { setNotFound(true); return null; }
        return r.json();
      })
      .then(data => { if (data?.post) setPost(data.post); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <PageBg>
      {/* Top bar */}
      <Box sx={{ py: 3, borderBottom: '1px solid #f5f5f5' }}>
        <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <Box component="img" src={logo} alt="Rewarding" sx={{ height: 36 }} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#111', letterSpacing: '-0.02em' }}>
              Rewarding
            </Typography>
          </Box>
          <Button onClick={() => navigate('/blogs')} startIcon={<ArrowBackIcon />} sx={{ textTransform: 'none', color: '#666' }}>
            All posts
          </Button>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 }, flex: 1 }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <CircularProgress size={32} sx={{ color: '#ffb71b' }} />
          </Box>
        ) : notFound || !post ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#111', mb: 1 }}>Post not found</Typography>
            <Typography sx={{ color: '#777', mb: 3 }}>This post may have been moved or unpublished.</Typography>
            <Button onClick={() => navigate('/blogs')} sx={{ textTransform: 'none' }}>Browse all posts</Button>
          </Box>
        ) : (
          <Article>
            <Box sx={{ mb: 4 }}>
              <Typography sx={{ fontSize: '0.9rem', color: '#999', fontWeight: 600, mb: 2 }}>
                {formatDate(post.publishedAt || post.createdAt)}
                {post.author ? ` • ${post.author}` : ''}
              </Typography>
              <Typography sx={{
                fontWeight: 900, fontSize: { xs: '2.2rem', md: '3rem' }, color: '#111',
                lineHeight: 1.15, mb: 3, letterSpacing: '-0.02em',
              }}>
                {post.title}
              </Typography>
              {post.excerpt && (
                <Typography sx={{ color: '#666', fontSize: '1.2rem', lineHeight: 1.6, mb: 4 }}>
                  {post.excerpt}
                </Typography>
              )}
              {post.coverImage && (
                <Box component="img" src={post.coverImage} alt={post.title} sx={{
                  width: '100%', borderRadius: 4, display: 'block', mb: 4,
                  boxShadow: '0 20px 50px rgba(0,0,0,0.08)',
                }} />
              )}
            </Box>

            <Box sx={{ '&::after': { content: '""', display: 'table', clear: 'both' } }}>
              {(post.blocks || []).map((block, i) => (
                <Block key={i} block={block} />
              ))}
            </Box>

            <Box sx={{ mt: 8, pt: 4, borderTop: '1px solid #eee', textAlign: 'center' }}>
              <Button onClick={() => navigate('/blogs')} startIcon={<ArrowBackIcon />} sx={{ textTransform: 'none', color: '#666' }}>
                Back to all posts
              </Button>
            </Box>
          </Article>
        )}
      </Container>
    </PageBg>
  );
}
